import React from 'react';
import LogoMain from '../LogoMain.png';
import { useOktaAuth } from '@okta/okta-react';
import { sanitizeReturnTo } from '../utils/returnTo';
import { SmsOptInContent, SmsTermsContent } from './SmsLegal';

const LoginPage = () => {
  const { oktaAuth } = useOktaAuth();

  // Which legal page (if any) is open in the in-page modal: 'optin' | 'terms'.
  // The content is rendered natively in React (see SmsLegal.jsx) rather than an
  // iframe/navigation to the server route — a stale/undeployed server serves the
  // SPA for those paths, which would recursively render the login page inside the
  // modal. Privacy Policy is third-party and still opens in a new tab.
  const [legalModal, setLegalModal] = React.useState(null); // null | 'optin' | 'terms'
  const legalTitle = legalModal === 'terms' ? 'SMS Terms & Conditions' : 'SMS Opt-In';

  React.useEffect(() => {
    if (!legalModal) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setLegalModal(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [legalModal]);

  const handleLogin = () => {
    // Clear the logout latch before bouncing to Okta. If we don't, a stale
    // 'lyric.justLoggedOut' from an earlier sign-out still in sessionStorage
    // will trip SecureRoute after the callback and force a SECOND login
    // before the app actually renders.
    try { sessionStorage.removeItem('lyric.justLoggedOut'); } catch { /* ignore */ }

    // Preserve where the user was headed. When a logged-out user opens a
    // deployed app (/apps/<name>/...), the server proxy bounces them here with
    // ?return_to=/apps/<name>/.... Okta v7's signInWithRedirect() does NOT
    // carry the query string through the PKCE round-trip (and restoreOriginalUri
    // sends us to '/'), so without stashing it the destination is lost and the
    // user lands on the Lyric landing page instead of the app they opened.
    // Stash it in sessionStorage (survives the Okta redirect, same tab/origin)
    // and let SecureRoute consume it AFTER the session cookie is established.
    // sanitizeReturnTo rejects anything that isn't a safe in-app /apps/ path
    // (dot-segment escapes, encoded variants, schemes, control chars) so a
    // crafted return_to can't be persisted and later normalized to /admin etc.
    try {
      const rt = sanitizeReturnTo(new URLSearchParams(window.location.search).get('return_to'));
      if (rt) {
        sessionStorage.setItem('lyric.returnTo', rt);
      }
    } catch { /* ignore */ }

    oktaAuth.signInWithRedirect();
  };

  // Request access: authenticate via Okta first (the login page is public-
  // facing), then land on the /request-access form. originalUri tells Okta's
  // restoreOriginalUri where to return after the PKCE callback.
  const handleRequestAccess = () => {
    try { sessionStorage.removeItem('lyric.justLoggedOut'); } catch { /* ignore */ }
    // Clear any stashed deployed-app return_to so the post-callback redirect
    // doesn't divert this flow to /apps/<name> instead of the request form.
    try { sessionStorage.removeItem('lyric.returnTo'); } catch { /* ignore */ }
    oktaAuth.signInWithRedirect({ originalUri: '/request-access' });
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        <div style={styles.logoSection}>
          <div style={styles.brandContainer}>
            <img 
              src={LogoMain}
              alt="RapidAI Logo" 
              style={styles.logo}
            />
            <h1 style={styles.appTitle}>Lyric</h1>
          </div>
          <p style={styles.appSubtitle}>
            Framework for Intelligent Development
          </p>
        </div>
        
        <div style={styles.loginSection}>
          <h2 style={styles.loginTitle}>Welcome Back</h2>
          <p style={styles.loginDescription}>
            Sign in with your organization account to access Lyric
          </p>
          
          <button onClick={handleLogin} style={styles.loginButton}>
            <span style={styles.buttonIcon}>🔐</span>
            Sign in with OKTA SSO
          </button>

          <p style={styles.requestAccessText}>
            Don't have access yet?{' '}
            <button onClick={handleRequestAccess} style={styles.requestAccessLink}>
              Request access to Lyric
            </button>
          </p>
          
          <div style={styles.features}>
            <h3 style={styles.featuresTitle}>What you can do with Lyric:</h3>
            <ul style={styles.featureList}>
              <li>📋 Create Epics with AI-generated stories</li>
              <li>📝 Generate and manage Jira issues</li>
              <li>🔬 Engineering review and validation</li>
              <li>🤖 AI-powered test case generation</li>
              <li>💬 Interactive AI assistance</li>
            </ul>
          </div>
        </div>
      </div>
      <footer style={styles.pageFooter}>
        <a
          href="/sms-optin"
          onClick={(e) => { e.preventDefault(); setLegalModal('optin'); }}
          style={styles.pageFooterLink}
        >SMS Opt-In</a>
        <span style={styles.pageFooterSep}>·</span>
        <a
          href="/sms-terms"
          onClick={(e) => { e.preventDefault(); setLegalModal('terms'); }}
          style={styles.pageFooterLink}
        >SMS Terms &amp; Conditions</a>
        <span style={styles.pageFooterSep}>·</span>
        <a href="https://www.rapidai.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.pageFooterLink}>Privacy Policy</a>
      </footer>

      {legalModal && (
        <div
          style={styles.modalBackdrop}
          onClick={() => setLegalModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label={legalTitle}
        >
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>{legalTitle}</span>
              <button
                type="button"
                onClick={() => setLegalModal(null)}
                style={styles.modalClose}
                aria-label="Close"
              >×</button>
            </div>
            <div style={styles.modalBody}>
              {legalModal === 'terms'
                ? <SmsTermsContent />
                : <SmsOptInContent onShowTerms={() => setLegalModal('terms')} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '2rem',
  },
  pageFooter: {
    marginTop: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '0.85rem',
  },
  pageFooterLink: {
    color: '#ffffff',
    textDecoration: 'underline',
  },
  pageFooterSep: {
    color: 'rgba(255, 255, 255, 0.6)',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    zIndex: 1000,
  },
  modalBox: {
    background: '#ffffff',
    borderRadius: '12px',
    width: 'min(820px, 100%)',
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
  },
  modalTitle: {
    fontWeight: 600,
    color: '#1a2233',
    fontSize: '1rem',
  },
  modalClose: {
    border: 'none',
    background: 'transparent',
    fontSize: '1.5rem',
    lineHeight: 1,
    cursor: 'pointer',
    color: '#64748b',
    padding: '0 0.25rem',
  },
  modalBody: {
    overflowY: 'auto',
    padding: '20px 24px 28px',
    flex: 1,
  },
  loginBox: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    maxWidth: '900px',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    minHeight: '600px',
  },
  logoSection: {
    background: 'linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%)',
    color: '#ffffff',
    padding: '3rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  appTitle: {
    fontSize: '3rem',
    margin: '0',
    fontWeight: 'bold',
    color: '#ffffff',
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
  },
  logo: {
    width: '60px',
    height: 'auto',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
  },
  appSubtitle: {
    fontSize: '1.2rem',
    opacity: 0.9,
    lineHeight: 1.5,
  },
  loginSection: {
    padding: '3rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  loginTitle: {
    fontSize: '2rem',
    color: '#333',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
  },
  loginDescription: {
    color: '#666',
    marginBottom: '2rem',
    lineHeight: 1.5,
  },
  loginButton: {
    backgroundColor: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '1rem 2rem',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.3s ease',
    marginBottom: '1rem',
  },
  buttonIcon: {
    fontSize: '1.2rem',
  },
  requestAccessText: {
    textAlign: 'center',
    color: '#666',
    fontSize: '0.95rem',
    margin: '0 0 2rem',
  },
  requestAccessLink: {
    background: 'none',
    border: 'none',
    color: '#007bff',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  features: {
    borderTop: '1px solid #e9ecef',
    paddingTop: '2rem',
  },
  featuresTitle: {
    fontSize: '1.1rem',
    color: '#333',
    marginBottom: '1rem',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  '@media (max-width: 768px)': {
    loginBox: {
      gridTemplateColumns: '1fr',
      maxWidth: '400px',
    },
    logoSection: {
      padding: '2rem',
    },
    loginSection: {
      padding: '2rem',
    },
  },
};

// Add hover effect using CSS-in-JS approach
styles.loginButton[':hover'] = {
  backgroundColor: '#0056b3',
  transform: 'translateY(-2px)',
  boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
};

export default LoginPage;
